import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Clock, ChevronDown, ChevronUp, Shield, ShieldCheck } from "lucide-react";
import { WaitTimeSourceLabel } from "@/components/WaitTimeSourceLabel";
import { LeaveByCalculator } from "./LeaveByCalculator";


interface WaitTimeData {
  standard: { waitTime: number; status: string; statusColor: string };
  precheck: { waitTime?: number; status?: string; statusColor?: string; available: boolean };
  lastUpdated: string;
  source?: string;
  confidence?: number;
  disclaimer?: string;
}

interface WaitTimeHeroProps {
  waitTime: WaitTimeData | null;
  loading: boolean;
  error: string | null;
  airportCode: string;
}

const getTrafficColor = (statusColor: string) => {
  switch (statusColor) {
    case "green": return { text: "text-success", bg: "bg-success/10", glow: "shadow-[0_0_40px_-10px_hsl(var(--success)/0.3)]" };
    case "yellow": return { text: "text-warning", bg: "bg-warning/10", glow: "shadow-[0_0_40px_-10px_hsl(var(--warning)/0.3)]" };
    case "red": return { text: "text-destructive", bg: "bg-destructive/10", glow: "shadow-[0_0_40px_-10px_hsl(var(--destructive)/0.3)]" };
    default: return { text: "text-primary", bg: "bg-primary/10", glow: "" };
  }
};

const getRelativeTime = (isoString: string) => {
  const diff = Math.floor((Date.now() - new Date(isoString).getTime()) / 60000);
  if (diff < 1) return "just now";
  if (diff === 1) return "1 min ago";
  return `${diff} min ago`;
};

export const WaitTimeHero = ({ waitTime, loading, error, airportCode }: WaitTimeHeroProps) => {
  // Open by default: the "when do I leave?" answer is the moment that earns the install.
  const [showCalculator, setShowCalculator] = useState(true);

  if (loading) {
    return (
      <Card className="shadow-lg">
        <CardContent className="py-8">
          <div className="animate-pulse space-y-4 text-center">
            <div className="h-6 w-48 rounded bg-muted skeleton-shimmer mx-auto" />
            <div className="h-16 w-40 rounded bg-muted skeleton-shimmer mx-auto" />
            <div className="h-4 w-32 rounded bg-muted skeleton-shimmer mx-auto" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !waitTime) {
    return (
      <Card className="shadow-lg">
        <CardContent className="py-8 text-center">
          <Clock className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-lg text-muted-foreground">
            {error || "Wait time data unavailable"}
          </p>
        </CardContent>
      </Card>
    );
  }

  const standardColors = getTrafficColor(waitTime.standard.statusColor);
  const precheckColors = waitTime.precheck.available && waitTime.precheck.statusColor
    ? getTrafficColor(waitTime.precheck.statusColor)
    : null;

  return (
    <Card className={`shadow-lg overflow-hidden ${standardColors.glow}`}>
      <CardContent className="py-6 md:py-8">
        {/* Header */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <Clock className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
          <h2 className="text-base font-medium text-muted-foreground">Current TSA Wait Time</h2>
        </div>

        {/* Wait Times Side by Side (stacks on very small screens) */}
        <div className="flex flex-col sm:flex-row items-stretch justify-center gap-6 sm:gap-0">
          {/* Standard */}
          <div className="flex-1 text-center px-4 md:px-8">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Shield className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <span className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Standard</span>
            </div>
            <div
              className={`text-6xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tabular-nums leading-none ${standardColors.text}`}
              aria-label={`${waitTime.standard.waitTime} minutes standard wait time`}
            >
              {waitTime.standard.waitTime}
            </div>
            <span className="text-sm text-muted-foreground mt-1 block">minutes</span>
            <div className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mt-3 ${standardColors.bg} ${standardColors.text}`}>
              {waitTime.standard.status} Traffic
            </div>
          </div>

          {/* Divider */}
          {waitTime.precheck.available && (
            <>
              <div className="hidden sm:block w-px bg-border self-stretch mx-2" />
              <div className="block sm:hidden h-px bg-border mx-8" />
            </>
          )}

          {/* PreCheck */}
          {waitTime.precheck.available && (
            <div className="flex-1 text-center px-4 md:px-8 sm:max-w-[50%]">
              <div className="flex items-center justify-center gap-2 mb-2">
                <ShieldCheck className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <span className="text-sm font-medium text-muted-foreground uppercase tracking-wide">PreCheck</span>
              </div>
              <div
                className={`text-4xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tabular-nums leading-none ${precheckColors?.text || "text-primary"}`}
                aria-label={`${waitTime.precheck.waitTime} minutes PreCheck wait time`}
              >
                {waitTime.precheck.waitTime}
              </div>
              <span className="text-sm text-muted-foreground mt-1 block">minutes</span>
              {waitTime.precheck.status && (
                <div className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mt-3 ${precheckColors?.bg} ${precheckColors?.text}`}>
                  {waitTime.precheck.status} Traffic
                </div>
              )}
            </div>
          )}
        </div>

        {/* Source + Updated in one line */}
        <div className="flex items-center justify-center gap-3 mt-6 text-xs text-muted-foreground">
          <WaitTimeSourceLabel
            source={waitTime.source as any}
            confidence={waitTime.confidence}
            disclaimer={waitTime.disclaimer}
            airportCode={airportCode}
          />
          <span>•</span>
          <span>Updated {getRelativeTime(waitTime.lastUpdated)}</span>
        </div>

        {/* Leave By Calculator Toggle */}
        <div className="mt-6 border-t border-border pt-4">
          <Button
            variant="ghost"
            className="w-full justify-center gap-2 text-primary hover:text-primary/80 min-h-[48px]"
            onClick={() => setShowCalculator(!showCalculator)}
          >
            <Clock className="h-4 w-4" />
            What time is your flight?
            {showCalculator ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>

          <div
            className={`overflow-hidden transition-all duration-300 ease-out ${
              showCalculator ? "max-h-[420px] opacity-100 mt-4" : "max-h-0 opacity-0"
            }`}
          >
            {/* Free on the web: the one-off calculation. Recurring alerts stay in the app/Pro. */}
            <LeaveByCalculator
              standardWait={waitTime.standard.waitTime}
              precheckWait={waitTime.precheck.waitTime || 0}
              airportCode={airportCode}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
